/**
 * Which step types spawn their fanout children DETACHED, for validation.
 *
 * `detachedFanout` is a hard-coded, step-type-level flag on a fanout SOURCE —
 * it never appears in a node's editable config, so nothing in the persisted
 * graph records it and `ValidationStep` (deliberately step-type-agnostic)
 * cannot see it. Validation still needs the answer: a detached region is
 * fire-and-forget, so an Await inside it has nothing to join, and the engine
 * refuses the topology at run time.
 *
 * Rather than copy the step-type strings, the set is DERIVED from the step
 * configs themselves, so the flag stays declared in exactly one place. The
 * imports are pure (zod schema + literals, no handlers), which keeps
 * validation loadable in the browser. `assertDetachedFanoutSetInSync` guards
 * the one thing derivation cannot: a NEW step type adding the flag without
 * being listed here.
 */

import { awsS3DataSourceAdapterStep } from "../steps/datasource-adapter/aws-s3-datasource-adapter.step";
import { blobStorageDataSourceAdapterStep } from "../steps/datasource-adapter/blob-storage-datasource-adapter.step";

/** Every step config known to carry the flag. Add new ones here. */
const CANDIDATE_SOURCES = [
  awsS3DataSourceAdapterStep,
  blobStorageDataSourceAdapterStep,
];

export const DETACHED_FANOUT_STEP_TYPES: ReadonlySet<string> = new Set(
  CANDIDATE_SOURCES.filter(
    (stepConfig) => stepConfig.detachedFanout === true,
  ).map((stepConfig) => stepConfig.type),
);

/** True when this step type's fanout children run detached (fire-and-forget). */
export const declaresDetachedFanout = (stepType: string): boolean =>
  DETACHED_FANOUT_STEP_TYPES.has(stepType);
